import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import { setModalxComponent, setModalxComponentProps, setModalxStyle } from "components/modalx/modalx.slice";
import { fetchPersonnelBusinessTrips, PersonnelBusinessTrip } from "services/personnel.service";
import { canOpenBusinessTrip, formatDay } from "./personnel-helpers";
import { panelClass, TablePagination } from "./PersonnelCardParts";

const cardClass = "rounded-lg border border-gray-200 bg-white shadow-sm";
const chipClass = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const thClass = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const tdClass = "px-3 py-2 align-top text-sm text-gray-700";
// Dropped on a phone, where the table does not fit; their content sits under the destination.
const hiddenOnPhone = "hidden md:table-cell";

function ReportStatusChip({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-gray-400">—</span>;
  const tone =
    status === "signed"
      ? "bg-green-50 text-green-800"
      : status === "rejected"
        ? "bg-red-50 text-red-700"
        : status === "annulled"
          ? "bg-gray-100 text-gray-600"
          : "bg-amber-50 text-amber-800";
  return (
    <span className={clsx(chipClass, tone)}>
      {t(`personnel_trip_report_status_${status}`, { defaultValue: status })}
    </span>
  );
}

/**
 * «Komandiruotės»: this person's business trips, read only, paginated. The
 * destination links into the business-trips module's own record only for a
 * viewer who holds that module's permission (Master / 'Access business
 * trips') — a head sees the tab but not the link, same as they never get a
 * row's `fileId`. That `fileId` is the trip's order document (main_trip), not
 * its report — shown as an «Įsakymas» / "Order" link next to the trip number,
 * opening through the usual PdfView modal.
 */
export default function PersonnelBusinessTripsTab({ companyId, userId }: { companyId: string; userId: string }) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const role = useAppSelector((state) => state.authReducer.role);
  const canOpen = canOpenBusinessTrip(role);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: PersonnelBusinessTrip[]; total: number; limit: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [companyId, userId]);

  const load = useCallback(() => {
    const requestId = ++requestRef.current;
    setFailed(false);
    fetchPersonnelBusinessTrips(companyId, userId, page)
      .then((result) => {
        if (requestId === requestRef.current) setData(result);
      })
      .catch(() => {
        if (requestId === requestRef.current) setFailed(true);
      });
  }, [companyId, userId, page]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const openDocument = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  if (failed) {
    return (
      <div className={clsx(panelClass, "flex items-center gap-3 text-sm text-red-700")}>
        {t("personnel_trips_load_failed")}
        <button type="button" className="underline" onClick={load}>
          {t("retry")}
        </button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-gray-500">{t("loading")}</p>;

  return (
    <section className={cardClass}>
      {data.rows.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">{t("personnel_trips_none")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                <th className={thClass}>{t("personnel_trip_destination")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_trip_purpose")}</th>
                <th className={thClass}>{t("personnel_trip_dates")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("status")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_trip_report")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((trip) => (
                <tr key={trip.id}>
                  <td className={tdClass}>
                    {canOpen ? (
                      <Link
                        to={`/app/${companyId}/business-trips/${trip.id}`}
                        className="font-medium text-blue-700 hover:underline max-md:inline-block max-md:py-1.5"
                      >
                        {trip.destination || "—"}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900">{trip.destination || "—"}</span>
                    )}
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                      {trip.number && <span>{t("personnel_trip_number", { number: trip.number })}</span>}
                      {trip.fileId && (
                        <button
                          type="button"
                          className="font-medium text-blue-700 hover:underline max-md:py-2"
                          onClick={() => openDocument(trip.fileId!)}
                        >
                          {t("personnel_trip_order")}
                        </button>
                      )}
                    </div>
                    <div className="mt-1 space-y-1 text-xs text-gray-600 md:hidden">
                      {trip.purpose && <div>{trip.purpose}</div>}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {trip.status && <span>{t(`status_${trip.status}`, { defaultValue: trip.status })}</span>}
                        <ReportStatusChip status={trip.reportStatus} />
                      </div>
                    </div>
                  </td>
                  <td className={clsx(tdClass, hiddenOnPhone)}>{trip.purpose || "—"}</td>
                  <td className={clsx(tdClass, "whitespace-nowrap tabular-nums")}>
                    {[formatDay(trip.startDate), formatDay(trip.endDate)].filter(Boolean).join(" – ") || "—"}
                  </td>
                  <td className={clsx(tdClass, hiddenOnPhone)}>{trip.status ? t(`status_${trip.status}`, { defaultValue: trip.status }) : "—"}</td>
                  <td className={clsx(tdClass, hiddenOnPhone)}>
                    <ReportStatusChip status={trip.reportStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TablePagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />
    </section>
  );
}
